import type { TerminalEvent } from "@cmmd-forge/contracts";
import {
  DEFAULT_THREAD_TERMINAL_HEIGHT,
  DEFAULT_THREAD_TERMINAL_ID,
  MAX_TERMINALS_PER_GROUP,
  type ThreadTerminalGroup,
} from "../types";

export const MIN_THREAD_TERMINAL_DRAWER_HEIGHT = 180;
export const MAX_THREAD_TERMINAL_DRAWER_HEIGHT_RATIO = 0.75;

export interface ResolvedThreadTerminalDrawerState {
  normalizedTerminalIds: string[];
  resolvedActiveTerminalId: string;
  resolvedTerminalGroups: ThreadTerminalGroup[];
  resolvedActiveGroupIndex: number;
  visibleTerminalIds: string[];
  hasTerminalSidebar: boolean;
  isSplitView: boolean;
  showGroupHeaders: boolean;
  hasReachedSplitLimit: boolean;
  terminalLabelById: Map<string, string>;
}

export interface ResolvedTerminalSelectionContext {
  terminalId: string;
  terminalLabel: string;
  lineStart: number;
  lineEnd: number;
  text: string;
}

export type TerminalEventAction =
  | { kind: "ignore" }
  | { kind: "write-output"; data: string }
  | {
      kind: "write-snapshot";
      snapshot: Extract<TerminalEvent, { type: "started" | "restarted" }>["snapshot"];
    }
  | { kind: "clear" }
  | { kind: "write-system-message"; message: string }
  | { kind: "session-exited"; message: string };

export function resolveTerminalSelectionContext(options: {
  terminalId: string;
  terminalLabel: string;
  selectionText: string;
  selectionStartY: number | undefined;
}): ResolvedTerminalSelectionContext | null {
  const normalizedText = options.selectionText.replace(/\r\n/g, "\n").replace(/^\n+|\n+$/g, "");
  if (options.selectionStartY === undefined || normalizedText.length === 0) {
    return null;
  }
  const lineStart = options.selectionStartY + 1;
  const lineCount = normalizedText.split("\n").length;
  return {
    terminalId: options.terminalId,
    terminalLabel: options.terminalLabel,
    lineStart,
    lineEnd: Math.max(lineStart, lineStart + lineCount - 1),
    text: normalizedText,
  };
}

export function resolveTerminalEventAction(event: TerminalEvent): TerminalEventAction {
  if (event.type === "activity") {
    return { kind: "ignore" };
  }

  if (event.type === "output") {
    return { kind: "write-output", data: event.data };
  }

  if (event.type === "started" || event.type === "restarted") {
    return { kind: "write-snapshot", snapshot: event.snapshot };
  }

  if (event.type === "cleared") {
    return { kind: "clear" };
  }

  if (event.type === "error") {
    return { kind: "write-system-message", message: event.message };
  }

  const details = [
    typeof event.exitCode === "number" ? `code ${event.exitCode}` : null,
    typeof event.exitSignal === "number" ? `signal ${event.exitSignal}` : null,
  ]
    .filter((value): value is string => value !== null)
    .join(", ");
  return {
    kind: "session-exited",
    message: details.length > 0 ? `Process exited (${details})` : "Process exited",
  };
}

export function resolveThreadTerminalDrawerHeight(options: {
  height: number;
  viewportHeight?: number | null | undefined;
}): number {
  const safeHeight = Number.isFinite(options.height)
    ? options.height
    : DEFAULT_THREAD_TERMINAL_HEIGHT;
  const maxHeight =
    typeof options.viewportHeight === "number" && Number.isFinite(options.viewportHeight)
      ? Math.max(
          MIN_THREAD_TERMINAL_DRAWER_HEIGHT,
          Math.floor(options.viewportHeight * MAX_THREAD_TERMINAL_DRAWER_HEIGHT_RATIO),
        )
      : DEFAULT_THREAD_TERMINAL_HEIGHT;
  return Math.min(Math.max(Math.round(safeHeight), MIN_THREAD_TERMINAL_DRAWER_HEIGHT), maxHeight);
}

export function resolveThreadTerminalWindowResizeState(options: {
  currentHeight: number;
  viewportHeight?: number | null | undefined;
}): { height: number; changed: boolean } {
  const height = resolveThreadTerminalDrawerHeight({
    height: options.currentHeight,
    viewportHeight: options.viewportHeight,
  });
  return {
    height,
    changed: height !== options.currentHeight,
  };
}

export function resolveThreadTerminalDrawerState(options: {
  terminalIds: ReadonlyArray<string>;
  activeTerminalId: string;
  terminalGroups: ReadonlyArray<ThreadTerminalGroup>;
  activeTerminalGroupId: string;
}): ResolvedThreadTerminalDrawerState {
  const { terminalIds, activeTerminalId, terminalGroups, activeTerminalGroupId } = options;
  const cleaned = [...new Set(terminalIds.map((id) => id.trim()).filter((id) => id.length > 0))];
  const normalizedTerminalIds = cleaned.length > 0 ? cleaned : [DEFAULT_THREAD_TERMINAL_ID];
  const resolvedActiveTerminalId = normalizedTerminalIds.includes(activeTerminalId)
    ? activeTerminalId
    : (normalizedTerminalIds[0] ?? DEFAULT_THREAD_TERMINAL_ID);
  const validTerminalIdSet = new Set(normalizedTerminalIds);
  const assignedTerminalIds = new Set<string>();
  const usedGroupIds = new Set<string>();
  const resolvedTerminalGroups: ThreadTerminalGroup[] = [];

  const assignUniqueGroupId = (groupId: string): string => {
    if (!usedGroupIds.has(groupId)) {
      usedGroupIds.add(groupId);
      return groupId;
    }
    let suffix = 2;
    while (usedGroupIds.has(`${groupId}-${suffix}`)) {
      suffix += 1;
    }
    const uniqueGroupId = `${groupId}-${suffix}`;
    usedGroupIds.add(uniqueGroupId);
    return uniqueGroupId;
  };

  for (const terminalGroup of terminalGroups) {
    const nextTerminalIds = [
      ...new Set(terminalGroup.terminalIds.map((id) => id.trim()).filter((id) => id.length > 0)),
    ].filter((terminalId) => {
      if (!validTerminalIdSet.has(terminalId)) return false;
      if (assignedTerminalIds.has(terminalId)) return false;
      return true;
    });
    if (nextTerminalIds.length === 0) continue;

    for (const terminalId of nextTerminalIds) {
      assignedTerminalIds.add(terminalId);
    }

    const baseGroupId =
      terminalGroup.id.trim().length > 0
        ? terminalGroup.id.trim()
        : `group-${nextTerminalIds[0] ?? DEFAULT_THREAD_TERMINAL_ID}`;
    resolvedTerminalGroups.push({
      id: assignUniqueGroupId(baseGroupId),
      terminalIds: nextTerminalIds,
    });
  }

  for (const terminalId of normalizedTerminalIds) {
    if (assignedTerminalIds.has(terminalId)) continue;
    resolvedTerminalGroups.push({
      id: assignUniqueGroupId(`group-${terminalId}`),
      terminalIds: [terminalId],
    });
  }

  if (resolvedTerminalGroups.length === 0) {
    resolvedTerminalGroups.push({
      id: `group-${resolvedActiveTerminalId}`,
      terminalIds: [resolvedActiveTerminalId],
    });
  }

  const indexById = resolvedTerminalGroups.findIndex(
    (terminalGroup) => terminalGroup.id === activeTerminalGroupId,
  );
  const indexByTerminal = resolvedTerminalGroups.findIndex((terminalGroup) =>
    terminalGroup.terminalIds.includes(resolvedActiveTerminalId),
  );
  const resolvedActiveGroupIndex =
    indexById >= 0 ? indexById : indexByTerminal >= 0 ? indexByTerminal : 0;
  const visibleTerminalIds = resolvedTerminalGroups[resolvedActiveGroupIndex]?.terminalIds ?? [
    resolvedActiveTerminalId,
  ];
  const hasTerminalSidebar = normalizedTerminalIds.length > 1;
  const isSplitView = visibleTerminalIds.length > 1;
  const showGroupHeaders =
    resolvedTerminalGroups.length > 1 ||
    resolvedTerminalGroups.some((terminalGroup) => terminalGroup.terminalIds.length > 1);
  const hasReachedSplitLimit = visibleTerminalIds.length >= MAX_TERMINALS_PER_GROUP;
  const terminalLabelById = new Map(
    normalizedTerminalIds.map((terminalId, index) => [terminalId, `Terminal ${index + 1}`]),
  );

  return {
    normalizedTerminalIds,
    resolvedActiveTerminalId,
    resolvedTerminalGroups,
    resolvedActiveGroupIndex,
    visibleTerminalIds,
    hasTerminalSidebar,
    isSplitView,
    showGroupHeaders,
    hasReachedSplitLimit,
    terminalLabelById,
  };
}

export function formatSplitTerminalActionLabel(options: {
  hasReachedSplitLimit: boolean;
  shortcutLabel?: string | undefined;
}): string {
  if (options.hasReachedSplitLimit) {
    return `Split Terminal (max ${MAX_TERMINALS_PER_GROUP} per group)`;
  }
  return options.shortcutLabel ? `Split Terminal (${options.shortcutLabel})` : "Split Terminal";
}

export function formatShortcutActionLabel(
  label: string,
  shortcutLabel?: string | undefined,
): string {
  return shortcutLabel ? `${label} (${shortcutLabel})` : label;
}

export function formatTerminalGroupLabel(options: {
  terminalCount: number;
  groupIndex: number;
}): string {
  return options.terminalCount > 1
    ? `Split ${options.groupIndex + 1}`
    : `Terminal ${options.groupIndex + 1}`;
}

export function formatCloseTerminalLabel(options: {
  terminalLabel: string | undefined;
  isActive: boolean;
  closeShortcutLabel?: string | undefined;
}): string {
  const baseLabel = `Close ${options.terminalLabel ?? "terminal"}`;
  return options.isActive && options.closeShortcutLabel
    ? `${baseLabel} (${options.closeShortcutLabel})`
    : baseLabel;
}
